<template>
  <FreeBottomSheet :name="name" @onOpen="handleBottomSheetOpen">
    <template #header="{ hide }">
      <FreeHeader class="p-4">
        <template #default>
          <h2
            class="text-text-neutral-primary text-base font-medium"
          >
            {{
              t('details.passenger.select', {
                passengerType: passengerTypeText,
              })
            }}
          </h2>
        </template>
        <template #rightAction>
          <FreeCloseButton
            @click="hide"
          />
        </template>
      </FreeHeader>
    </template>
    <template #default="{ hide }">
      <div
        v-if="isLoading"
        class="flex min-h-[80vh] items-center justify-center"
      >
        <FreeLoader class="green-spinner" />
      </div>
      <div v-else class="flex w-full flex-col items-center justify-center">
        <section
          v-if="passengers.length > 0"
          class="w-full space-y-3 px-4 pb-40"
        >
          <PassengerRadioButton
            v-for="(passenger, idx) in passengers"
            :id="passenger.document.number"
            ref="savedPassengersListRef"
            :key="idx"
            :pax-idx="idx"
            :name="SAVED_PASSENGERS_RADIO_NAME"
            :checked="isPassengerSelected(passenger)"
            :passenger="passenger"
            @onPassengerEditClick="onPassengerEditClick(passenger, idx)"
            @change="onPassengerSelected(passenger)"
          />
        </section>
        <NoSavedPassengers
          v-else
          without-action
          class="pb-40"
          @onCloseBottomSheet="hide"
        />
        <div
          class="w-full space-y-4 px-4 pb-8"
          :class="{
            'buttons-shadow bg-background-neutral-secondary fixed bottom-0 rounded-t-2xl pt-4': true,
          }"
        >
          <FreeButton
            type="button"
            class="gray-button w-full"
            @click="onAddNewPassengerClick"
          >
            {{ t('details.passenger.addNew') }}
          </FreeButton>
          <FreeButton
            type="button"
            class="green-main-button w-full"
            :disabled="!hasSelectedPassenger"
            @click="onSelectPassenger"
          >
            {{ t('general.select') }}
          </FreeButton>
        </div>
      </div>
    </template>
  </FreeBottomSheet>
</template>

<script setup lang="ts">
  import { ref, nextTick, watch, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { isEqual } from 'lodash-es';
  import {
    FreeBottomSheet,
    FreeButton,
    FreeCloseButton,
    FreeHeader,
    FreeLoader,
    NoSavedPassengers,
  } from 'freedom-shared';
  import { controllerModel } from 'shared';
  import type { Passenger } from '@aviata/booking-data';

  import { bookingStateModel } from '@/features/booking-state';

  import { analyticsEventsLib } from '@/shared/lib';

  import PassengerRadioButton from './PassengerRadioButton.vue';
  import { SAVED_PASSENGERS_RADIO_NAME } from '../config';

  const props = defineProps<{
    name: string;
    passengers: Passenger[];
    passengerOnCard: Passenger | null;
    isLoading: boolean;
    passengerType: string;
    providerClass?: string;
  }>();

  const emit = defineEmits<{
    onPassengerUpdate: [passenger: Passenger];
    onPassengerFormOpen: [idx: number | null];
    /** Пользователь нажал «Добавить нового пассажира» — выбрать способ ввода (фото / вручную) */
    onAddNewPassenger: [];
  }>();

  const { t } = useI18n();
  const bookingStore = bookingStateModel.useBookingStore();

  const savedPassengersListRef = ref<(typeof PassengerRadioButton)[] | null>(
    null,
  );
  const selectedPassenger = ref<Passenger | null>(props.passengerOnCard);
  const hasSelectedPassenger = computed(() => {
    return Boolean(selectedPassenger.value?.firstName);
  });

  const localizedPassengerTypes = {
    ADT: t('passengersData.passengerTypes.ADT.text'),
    CHD: t('passengersData.passengerTypes.CHD.text'),
    INF: t('passengersData.passengerTypes.INF.text'),
    YTH: t('passengersData.passengerTypes.YTH.text'),
  };

  const passengerTypeText = computed(() => {
    return localizedPassengerTypes[
      props.passengerType as keyof typeof localizedPassengerTypes
    ];
  });

  const isPassengerSelected = (passenger: Passenger) => {
    return props.passengerOnCard?.id === passenger?.id;
  };

  function onPassengerSelected(passenger: Passenger) {
    selectedPassenger.value = passenger;
  }

  const nullifyProductsInSessionStorage = () => {
    bookingStore.update({
      products: { state: null, products: null },
    });
  };

  function onSelectPassenger() {
    if (selectedPassenger.value?.firstName) {
      analyticsEventsLib.selectSavedPassenger(props.providerClass);

      if (!isEqual(selectedPassenger.value, props.passengerOnCard)) {
        nullifyProductsInSessionStorage();
      }

      emit('onPassengerUpdate', selectedPassenger.value);
    }
  }

  const onPassengerEditClick = (passenger: Passenger, idx: number) => {
    analyticsEventsLib.savedPaxEditClick({
      passenger,
      index: idx,
      providerClass: props.providerClass,
    });
    emit('onPassengerFormOpen', idx);
  };

  const onAddNewPassengerClick = () => {
    analyticsEventsLib.addNewPaxClick(props.providerClass);
    emit('onAddNewPassenger');
  };

  const scrollToSelectedPassenger = () => {
    const elementToScrollTo = savedPassengersListRef.value?.find(el => {
      return (
        el.$el.getAttribute('id') === selectedPassenger.value?.document.number
      );
    })?.$el;
    elementToScrollTo?.scrollIntoView({ block: 'center' });
  };

  const handleBottomSheetOpen = () => {
    selectedPassenger.value = props.passengerOnCard;
    nextTick(scrollToSelectedPassenger);
  };

  watch(
    () => props.passengerOnCard,
    () => {
      selectedPassenger.value = props.passengerOnCard;
      nextTick(scrollToSelectedPassenger);
    },
  );
</script>

<style scoped>
  .buttons-shadow {
    box-shadow: 0px -6px 20px 0px #0000000a;
  }
</style>
